import { isClientEnvironment, openMailToSupport, openHelpCenterArticleInNewTab } from './utils'
import config from 'shared/config'
import logger from 'shared/logger'
import { type IntercomSpaceId, type IntercomArticleId } from './constants/documentation'
import { type IntercomErrorId } from './configStatuses'
import { type Client } from './context/ClientContext'
import { type UserDetailsResp } from './api/clients'

const log = logger().child({
  namespace: 'intercom'
})

const APP_ID = config('NEXT_PUBLIC_INTERCOM_APP_ID') as string

export const boot = ({ user, client }: { user: UserDetailsResp; client: Client }): void => {
  if (isClientEnvironment() && window?.Intercom != null) {
    try {
      window.Intercom('boot', {
        app_id: APP_ID,
        ...getUserPropertiesForIntercom(user),
        company: {
          company_id: client.id,
          name: client.name,
          website: client.url
        }
      })
      log.info('Intercom booted')
    } catch (e) {
      log.error(`Intercom boot failed: ${(e as Error).message}`)
    }
  }
}

export const shutdown = (): void => {
  if (isClientEnvironment() && window?.Intercom != null) {
    try {
      window.Intercom('shutdown')
      log.info('Intercom shutdown')
    } catch (e) {
      log.error(`Intercom shutdown failed: ${(e as Error).message}`)
    }
  }
}

export const update = ({ user, client }: { user: UserDetailsResp; client: Client }): void => {
  if (isClientEnvironment() && window?.Intercom != null) {
    try {
      window.Intercom('update', {
        ...getUserPropertiesForIntercom(user),
        company: {
          company_id: client.id,
          name: client.name,
          website: client.url
        }
      })
      log.info('Intercom updated')
    } catch (e) {
      log.error(`Intercom update failed: ${(e as Error).message}`)
    }
  }
}

export const openIntercomArticle = (
  articleId: IntercomArticleId | IntercomErrorId,
  onError:
    | ((articleId: IntercomArticleId | IntercomErrorId, error?: Error) => void)
    | undefined = openHelpCenterArticleInNewTab
) => {
  return (): void => {
    try {
      window.Intercom('showArticle', articleId)
    } catch (e) {
      log.error(`Intercom showArticle failed: ${(e as Error).message}`)
      onError?.(articleId, e as Error)
    }
  }
}

export const openIntercomSpace = (
  spaceId: IntercomSpaceId,
  onError: ((spaceId: IntercomSpaceId, error?: Error) => void) | undefined = undefined
) => {
  return (): void => {
    try {
      window.Intercom('showSpace', spaceId)
    } catch (e) {
      log.error(`Intercom showSpace failed: ${(e as Error).message}`)
      onError?.(spaceId, e as Error)
    }
  }
}

export const openIntercomNewMessage = (
  prePopulatedMessage?: string,
  onError: ((prePopulatedMessage?: string, error?: Error) => void) | undefined = openMailToSupport
) => {
  return (): void => {
    try {
      window.Intercom('showNewMessage', prePopulatedMessage)
    } catch (e) {
      log.error(`Intercom showNewMessage failed: ${(e as Error).message}`)
      onError?.(prePopulatedMessage, e as Error)
    }
  }
}
/**
 * Gets properties we want to send to Intercom from our UserDetailsResp
 * Note: Intercom expects timestamps in seconds since UNIX, not milliseconds
 * @param user The user details of the user to get the properties for
 * @returns The properties for the user to be used in Intercom
 */
const getUserPropertiesForIntercom = (user: UserDetailsResp): Intercom_.IntercomSettings => {
  return {
    email: user.email,
    user_id: user.id.toString(),
    name: user.name,
    phone: user.phone,
    created_at: Math.floor(new Date(user.createdAt).getTime() / 1000),

    ...(user.avatarUrl != null
      ? {
          avatar: {
            type: 'avatar',
            image_url: user.avatarUrl
          }
        }
      : {}),

    // The following are custom attributes not available by default in Intercom
    job_title: user.jobTitle,
    has_access_to_internal_features: user.hasInternalFeatureAccess,
    show_internal_features: user.showInternalFeatures,
    system_role: user.system_role,
    updated_at: Math.floor(new Date(user.updatedAt).getTime() / 1000)
  }
}
